/**
 * useDomainPrimaryBadge — single hook the main-menu tile reads to render
 * its live count.
 *
 * Each domain owns one "primary badge" — the most actionable count to
 * surface at app-shell level. Returns `{ count, color }` when the value
 * is greater than zero, otherwise `null` so the tile doesn't render a
 * "0" badge.
 */

import type { DomainKey } from '@/config/domains';
import { useHrBadgesStore } from './hrBadgesStore';
import { useProcurementBadgesStore } from './procurementBadgesStore';
import { useProjectBadgesStore } from './projectBadgesStore';
import { useResourceBadgesStore } from './resourceBadgesStore';
import { useTaskBadgesStore } from './taskBadgesStore';
import { useAnnouncementBadgesStore } from './announcementBadgesStore';
import { useBookingBadgesStore } from './bookingBadgesStore';
import { useVendorBadgesStore } from './vendorBadgesStore';
import { useUserBadgesStore } from './userBadgesStore';
import { useLeaveApprovalBadgesStore } from './leaveApprovalBadgesStore';

export interface DomainPrimaryBadge {
  count: number;
  color: string;
}

/**
 * Hooks must be called unconditionally and in a stable order, so we
 * subscribe to every store on every render. The selector pulls just the
 * scalar we need from each store — no full state reads.
 */
export function useDomainPrimaryBadge(
  domainKey: DomainKey | string,
): DomainPrimaryBadge | null {
  const hr = useHrBadgesStore((s) => s.pendingLeaves);
  const procurement = useProcurementBadgesStore((s) => s.pendingApprovals);
  const project = useProjectBadgesStore((s) => s.myProjects);
  const resource = useResourceBadgesStore((s) => s.coursesInDraft);
  const task = useTaskBadgesStore((s) => s.unseenAssigned);
  const announcement = useAnnouncementBadgesStore((s) => s.drafts);
  const booking = useBookingBadgesStore((s) => s.pendingApprovals);
  const vendor = useVendorBadgesStore((s) => s.pendingVendors);

  let count = 0;
  let color = 'gray';

  switch (domainKey) {
    case 'members':
      count = hr;
      color = 'orange';
      break;
    case 'procurements':
      count = procurement;
      color = 'red';
      break;
    case 'projects':
      count = project;
      color = 'blue';
      break;
    case 'resources':
      count = resource;
      color = 'yellow';
      break;
    case 'tasks':
      count = task;
      color = 'orange';
      break;
    case 'announcements':
      count = announcement;
      color = 'yellow';
      break;
    case 'bookings':
      count = booking;
      color = 'orange';
      break;
    case 'vendors':
      count = vendor;
      color = 'orange';
      break;
    default:
      return null;
  }

  if (count <= 0) return null;
  return { count, color };
}

/**
 * Sync counterpart to `useDomainPrimaryBadge` — readable from inside a
 * `countSelector` callback (which must be synchronous, no hooks). Reads
 * each store via `getState()` and returns the primary badge count for
 * the given domain. Returns `null` when the count is 0 or the domain
 * has no badge mapping.
 */
export function getDomainPrimaryBadgeCount(
  domainKey: DomainKey | string,
): number | null {
  switch (domainKey) {
    case 'members':       return useHrBadgesStore.getState().pendingLeaves || null;
    case 'procurements':  return useProcurementBadgesStore.getState().pendingApprovals || null;
    case 'projects':      return useProjectBadgesStore.getState().myProjects || null;
    case 'resources':     return useResourceBadgesStore.getState().coursesInDraft || null;
    case 'tasks':         return useTaskBadgesStore.getState().unseenAssigned || null;
    case 'announcements': return useAnnouncementBadgesStore.getState().drafts || null;
    case 'bookings':      return useBookingBadgesStore.getState().pendingApprovals || null;
    case 'vendors':       return useVendorBadgesStore.getState().pendingVendors || null;
    default:              return null;
  }
}

/**
 * Per-domain primary badge color. Stays in sync with the table inside
 * `useDomainPrimaryBadge`.
 */
export function getDomainPrimaryBadgeColor(
  domainKey: DomainKey | string,
): string | null {
  switch (domainKey) {
    case 'members':       return 'orange';
    case 'procurements':  return 'red';
    case 'projects':      return 'blue';
    case 'resources':     return 'yellow';
    case 'tasks':         return 'orange';
    case 'announcements': return 'yellow';
    case 'bookings':      return 'orange';
    case 'vendors':       return 'orange';
    default:              return null;
  }
}

/**
 * useAllBadgeStoresRevision — subscribes the caller to every domain's
 * badge stores and returns a single scalar that increments whenever any
 * count changes. The MenuRenderer uses this so it re-renders any time a
 * countSelector's underlying value moves.
 *
 * The returned number itself isn't meaningful — it's a change signal.
 */
export function useAllBadgeStoresRevision(): number {
  const hr = useHrBadgesStore(
    (s) => s.pendingLeaves + s.onLeaveToday + s.policiesAwaiting + s.contractsExpiring + s.myLeavesActionNeeded,
  );
  const procurement = useProcurementBadgesStore(
    (s) => s.pendingApprovals + s.myRequestsInFlight,
  );
  const project = useProjectBadgesStore(
    (s) => s.myProjects + s.overdueMilestones,
  );
  const resource = useResourceBadgesStore(
    (s) => s.coursesInDraft + s.kbInDraft,
  );
  const task = useTaskBadgesStore(
    (s) => s.unseenAssigned + s.overdueAssigned,
  );
  const announcement = useAnnouncementBadgesStore(
    (s) => s.drafts + s.upcoming,
  );
  const booking = useBookingBadgesStore(
    (s) => s.pendingApprovals + s.overdue,
  );
  const vendor = useVendorBadgesStore(
    (s) => s.pendingVendors + s.draftProducts + s.pendingRatingResponses,
  );
  const users = useUserBadgesStore((s) => s.lockedOut);
  // Leave approvals: `revision` ticks on every write, so the menu re-renders when
  // the 0/1 visibility flags flip even if the counters' sum stays the same.
  const leaveApprovals = useLeaveApprovalBadgesStore(
    (s) =>
      s.revision +
      s.myPendingApprovals +
      s.finalQueue +
      s.myWaitingOnEmployee +
      s.isLineApprover +
      s.isFinalApprover +
      s.isSoleFinalApprover,
  );

  return hr + procurement + project + resource + task + announcement + booking + vendor + users + leaveApprovals;
}
